import {Action, Selector, State, StateContext, Store} from "@ngxs/store";
import {Injectable} from "@angular/core";
import {CompoApp} from "../../services/CompoApp";
import {Dialog2Service} from "../../services/dialog2.service";
import {UserAreaEditorActions} from "./user-area-editor.action";
import {UserAreaType} from "../../models/rights/user-area/user-area-type.enum";
import {Observable} from "rxjs";
import {map, tap} from "rxjs/operators";
import {UserAreaService} from "../../services/user-area.service";
import {UserAreaEditorDialog} from "../../models/rights/user-area/user-area-import-dialog.model";
import {UserAreaDTO} from "../../models/rights/user-area/UserAreaDTO";
import {ResetStateDefaults} from "../base-action-class.model";
import { UserAreaDetailMenuEnum } from '../../enums/rights/UserAreaDetailMenuEnum';
import { ClassUtils } from '../../utils/ClassUtils';
import { Dialog2Type } from '../../models/dialog/dialog2.model';

interface UserAreaEditorStateModel {
  open: boolean;
  userAreaId: string;
  userArea: UserAreaDTO;
  mode: UserAreaEditorDialog.Mode;
  type: UserAreaType;
  page: UserAreaEditorDialog.Page;
  selectionType: UserAreaEditorDialog.SelectionType;
  selectedClassIds: Set<string>;
  allClassIds: Set<string>;
  selectedOrganizationIds: Set<string>;
  selectedLocationIds: Set<string>;
  selectedObjectIds: Set<string>;
  selectedModuleIds: Set<string>;
  stepCounter: number;
  saveInProgress: boolean;
  allOrganizationCount: number;
  allLocationCount: number;
  allClassCount: number;
  allObjectCount: number;
  allModuleCount: number;
}

const getDefaults = (): UserAreaEditorStateModel => ({
  open: false,
  userAreaId: null,
  userArea: null,
  type: null,
  mode: null,
  page: null,
  selectionType: null,
  selectedClassIds: new Set(),
  allClassIds: new Set(),
  selectedOrganizationIds: new Set(),
  selectedLocationIds: new Set(),
  selectedObjectIds: new Set(),
  selectedModuleIds: new Set(),
  stepCounter: 1,
  saveInProgress: false,
  allOrganizationCount: null,
  allLocationCount: null,
  allClassCount: null,
  allObjectCount: null,
  allModuleCount: null
});

@Injectable()
@State<UserAreaEditorStateModel>({
  name: 'userAreaEditor',
  defaults: getDefaults()
})
export class UserAreaEditorXsState {

  constructor(
    private app: CompoApp,
    private dialog2service: Dialog2Service,
    private store: Store,
    private userAreaService: UserAreaService
  ) { }

  //region SELECTOR
  @Selector()
  static mode(state: UserAreaEditorStateModel): UserAreaEditorDialog.Mode {
    return state.mode;
  }

  @Selector()
  static userArea(state: UserAreaEditorStateModel): UserAreaDTO {
    return state.userArea;
  }

  @Selector()
  static userAreaId(state: UserAreaEditorStateModel): string {
    return state.userAreaId;
  }

  @Selector()
  static page(state: UserAreaEditorStateModel): UserAreaEditorDialog.Page {
    return state.page;
  }

  @Selector()
  static stepCounter(state: UserAreaEditorStateModel): number {
    return state.stepCounter;
  }

  @Selector()
  static stepForwardPossible(state: UserAreaEditorStateModel): boolean {
    if (!state.mode || !state.type || !state.page) {
      return false;
    }

    switch (state.page) {
      case UserAreaEditorDialog.Page.SELECTION_TYPE: {
        return true;
      }
    }

    return false;
  }

  @Selector()
  static stepForwardEnabled(state: UserAreaEditorStateModel): boolean {
    if (!state.mode || !state.type || !state.page || state.saveInProgress) {
      return false;
    }

    switch (state.page) {
      case UserAreaEditorDialog.Page.SELECTION_TYPE: {
        return !!state.selectionType;
      }
    }

    return false;
  }

  @Selector()
  static stepBackPossible(state: UserAreaEditorStateModel): boolean {
    if (!state.mode || !state.type || !state.page) {
      return false;
    }

    switch (state.page) {
      case UserAreaEditorDialog.Page.SELECT_ORGANIZATION:
      case UserAreaEditorDialog.Page.SELECT_ORGANIZATION_AND_LOCATION:
      case UserAreaEditorDialog.Page.SELECT_CLASSES: {
        return true;
      }
    }

    return false;
  }

  @Selector()
  static savePossible(state: UserAreaEditorStateModel): boolean {
    if (!state.mode || !state.type || !state.page) {
      return false;
    }

    switch (state.page) {
      case UserAreaEditorDialog.Page.SELECT_OBJECTS:
      case UserAreaEditorDialog.Page.SELECT_CLASSES:
      case UserAreaEditorDialog.Page.SELECT_ORGANIZATION:
      case UserAreaEditorDialog.Page.SELECT_MODULES:
      case UserAreaEditorDialog.Page.SELECT_ORGANIZATION_AND_LOCATION: {
        return true;
      }
    }

    return false;
  }

  @Selector()
  static saveEnabled(state: UserAreaEditorStateModel): boolean {
    if (!state.mode || !state.type || !state.page) {
      return false;
    }

    switch (state.type) {
      case UserAreaType.COMBINATION: {
        if (state.selectionType) {
          switch (state.selectionType) {
            case UserAreaEditorDialog.SelectionType.ORG_LOCATION: {
              return state.selectedOrganizationIds.size > 0 || state.selectedLocationIds.size > 0;
            }
            case UserAreaEditorDialog.SelectionType.CLASS: {
              return state.selectedClassIds.size > 0;
            }
          }
        }
        break;
      }
      case UserAreaType.SINGLE_SELECTION_OBJECT: {
        return state.selectedObjectIds.size > 0;
      }
      case UserAreaType.SINGLE_SELECTION_MODULE: {
        return state.selectedModuleIds.size > 0;
      }
    }

    return false;
  }

  @Selector()
  static selectionType(state: UserAreaEditorStateModel): UserAreaEditorDialog.SelectionType {
    return state.selectionType;
  }

  @Selector()
  static selectedClassIds(state: UserAreaEditorStateModel): Set<string> {
    return state.selectedClassIds || new Set();
  }

  @Selector()
  static selectedOrganizationIds(state: UserAreaEditorStateModel): Set<string> {
    return state.selectedOrganizationIds || new Set();
  }

  @Selector()
  static selectedLocationIds(state: UserAreaEditorStateModel): Set<string> {
    return state.selectedLocationIds || new Set();
  }

  @Selector()
  static selectedObjectIds(state: UserAreaEditorStateModel): Set<string> {
    return state.selectedObjectIds || new Set();
  }

  @Selector()
  static saveInProgress(state: UserAreaEditorStateModel): boolean {
    return state.saveInProgress;
  }

  @Selector()
  static allOrganizationCount(state: UserAreaEditorStateModel): number {
    return state.allOrganizationCount || 0;
  }

  @Selector()
  static allLocationCount(state: UserAreaEditorStateModel): number {
    return state.allLocationCount || 0;
  }

  @Selector()
  static allClassCount(state: UserAreaEditorStateModel): number {
    return state.allClassCount || 0;
  }

  @Selector()
  static allObjectCount(state: UserAreaEditorStateModel): number {
    return state.allObjectCount || 0;
  }

  @Selector()
  static allModuleCount(state: UserAreaEditorStateModel): number {
    return state.allModuleCount || 0;
  }

  @Selector()
  static selectedOrganizationCount(state: UserAreaEditorStateModel): number {
    return state.selectedOrganizationIds.size;
  }

  @Selector()
  static selectedLocationCount(state: UserAreaEditorStateModel): number {
    return state.selectedLocationIds.size;
  }

  @Selector()
  static selectedClassCount(state: UserAreaEditorStateModel): number {
    return state.selectedClassIds.size;
  }

  @Selector()
  static selectedObjectCount(state: UserAreaEditorStateModel): number {
    return state.selectedObjectIds.size;
  }

  @Selector()
  static selectedModuleCount(state: UserAreaEditorStateModel): number {
    return state.selectedModuleIds.size;
  }

  @Selector()
  static userAreaFullDataOrganizationIds(state: UserAreaEditorStateModel): Set<string> {
    return new Set(state.userArea?.organizations
      ?.filter(entry => entry.fullData)
      .map(entry => entry.organizationId) || []);
  }

  @Selector()
  static userAreaFullDataLocationIds(state: UserAreaEditorStateModel): Set<string> {
    return new Set(state.userArea?.locations
      ?.filter(entry => entry.fullData)
      .map(entry => entry.locationId) || []);
  }

  @Selector()
  static userAreaFullDataClassIds(state: UserAreaEditorStateModel): Set<string> {
    return new Set(state.userArea?.classes
      ?.filter(entry => entry.fullData)
      .map(entry => entry.componentClassId) || []);
  }

  @Selector()
  static userAreaFullDataObjectIds(state: UserAreaEditorStateModel): Set<string> {
    return new Set(state.userArea?.objects
      ?.filter(entry => entry.fullData)
      .map(entry => entry.objectId) || []);
  }
  //endregion

  //region ACTION
  @Action(UserAreaEditorActions.OpenDialog)
  public openDialog(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.OpenDialog
  ): void {

    if (ctx.getState().open) {
      throw new Error('Dialog is already opened!');
    }

    if (!action.mode) {
      throw new Error('Mode cannot be null!');
    }

    const userAreaId = action.userArea.id;
    const type: UserAreaType = action.userArea.type;

    if (!userAreaId) {
      throw new Error('User area id cannot be null!');
    }

    if (!type) {
      throw new Error('User area type cannot be null!');
    }

    if (type === UserAreaType.NONE) {
      throw new Error('Cannot initialize dialog with type "NONE"!');
    }

    let page: UserAreaEditorDialog.Page;

    switch (type) {
      case UserAreaType.COMBINATION: {
        page = UserAreaEditorDialog.Page.SELECTION_TYPE;
        break;
      }
      case UserAreaType.SINGLE_SELECTION_OBJECT: {
        page = UserAreaEditorDialog.Page.SELECT_OBJECTS;
        break;
      }
      case UserAreaType.SINGLE_SELECTION_MODULE: {
        page = UserAreaEditorDialog.Page.SELECT_MODULES;
        break;
      }
    }

    let allOrganizationCount = null;
    let allLocationCount = null;
    let allClassCount = null;
    let allObjectCount = null;
    let allModuleCount = null;

    if (action.mode === UserAreaEditorDialog.Mode.REMOVE) {
      allOrganizationCount
        = action.userArea.organizations?.filter(entry => entry.fullData).length;
      allLocationCount
        = action.userArea.locations?.filter(entry => entry.fullData).length;
      allClassCount
        = action.userArea.classes?.filter(entry => entry.fullData && entry.componentClassId != ClassUtils.INSTANCE_ROOT_CLASS_ID).length;
      allObjectCount
        = action.userArea.objects?.filter(entry => entry.fullData).length;
      allModuleCount
        = action.userArea.moduleIds.length;
    }

    this.dialog2service.openDialog(Dialog2Type.USER_AREA_EDITOR);

    ctx.patchState({
      ...(getDefaults()),
      open: true,
      mode: action.mode,
      userAreaId,
      userArea: action.userArea,
      stepCounter: 1,
      page,
      type: type,
      allOrganizationCount,
      allLocationCount,
      allClassCount,
      allObjectCount,
      allModuleCount
    });
  }

  @Action(UserAreaEditorActions.StepForward)
  public stepForward(
    ctx: StateContext<UserAreaEditorStateModel>
  ): void {
    const state = ctx.getState();

    if (!state.type || !state.page) {
      throw new Error('Cannot go forward when type or page is not set!');
    }

    this.checkIfSaveIsInProgress(state);

    let nextPage: UserAreaEditorDialog.Page;

    switch (state.page) {
      case UserAreaEditorDialog.Page.SELECTION_TYPE: {
        if (state.mode && state.selectionType) {
          switch (state.selectionType) {
            case UserAreaEditorDialog.SelectionType.ORG_LOCATION: {
              nextPage = UserAreaEditorDialog.Page.SELECT_ORGANIZATION_AND_LOCATION;
              break;
            }
            case UserAreaEditorDialog.SelectionType.CLASS: {
              nextPage = UserAreaEditorDialog.Page.SELECT_CLASSES;
              break;
            }
          }
        }
        break;
      }
    }

    if (!nextPage) {
      throw new Error('Going forward is not enabled!');
    }

    ctx.patchState({
      stepCounter: state.stepCounter + 1,
      page: nextPage
    });
  }

  @Action(UserAreaEditorActions.StepBack)
  public stepBack(
    ctx: StateContext<UserAreaEditorStateModel>
  ): void {
    const state = ctx.getState();

    this.checkIfSaveIsInProgress(state);

    if (!state.type || !state.page) {
      throw new Error('Cannot go back when type or page is not set!');
    }

    let prevPage: UserAreaEditorDialog.Page;
    let updatedStateProperties: Partial<UserAreaEditorStateModel>;

    if (!UserAreaEditorXsState.stepBackPossible(state)) {
      throw new Error('Navigation back is not enabled!');
    }

    switch (state.page) {
      case UserAreaEditorDialog.Page.SELECT_ORGANIZATION:
      case UserAreaEditorDialog.Page.SELECT_LOCATION:
      case UserAreaEditorDialog.Page.SELECT_ORGANIZATION_AND_LOCATION: {
        updatedStateProperties = {
          selectedOrganizationIds: new Set(),
          selectedLocationIds: new Set(),
        };
        switch (state.type) {
          case UserAreaType.COMBINATION: {
            prevPage = UserAreaEditorDialog.Page.SELECTION_TYPE;
            break;
          }
        }
        break;
      }
      case UserAreaEditorDialog.Page.SELECT_CLASSES: {
        if (state.type === UserAreaType.COMBINATION) {
          updatedStateProperties = {
            selectedClassIds: new Set()
          };
          switch (state.type) {
            case UserAreaType.COMBINATION: {
              prevPage = UserAreaEditorDialog.Page.SELECTION_TYPE;
              break;
            }
          }
        }
        break;
      }
    }

    if (!prevPage) {
      throw new Error('Navigation back is not enabled!');
    }

    ctx.patchState({
      stepCounter: state.stepCounter - 1,
      page: prevPage,
      ...updatedStateProperties
    });
  }

  @Action(UserAreaEditorActions.SetSelectionType)
  public setSelectionType(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetSelectionType
  ): void {
    const state = ctx.getState();

    this.checkIfSaveIsInProgress(state);

    if (!action.selectionType) {
      throw new Error('Selection type cannot be null!');
    }

    if (!UserAreaEditorDialog.SelectionType[action.selectionType]) {
      throw new Error('The selected value "' + action.selectionType + '" is not a valid selection type!');
    }

    if (UserAreaEditorDialog.Page.SELECTION_TYPE !== state.page) {
      throw new Error('Cannot set selectiontype if page "' + state.page + '" is active!');
    }

    ctx.patchState({
      selectionType: action.selectionType,
    });
  }

  @Action(UserAreaEditorActions.SetOrganizationSelection)
  public setOrganizationSelection(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetOrganizationSelection
  ): void {
    const state = ctx.getState();

    this.checkIfSaveIsInProgress(state);

    if (!action.selectedOrganizationIds) {
      throw new Error('Selected class ids cannot be null!');
    }

    this.checkErrorsForSelection(state);

    if (![UserAreaEditorDialog.Page.SELECT_ORGANIZATION, UserAreaEditorDialog.Page.SELECT_ORGANIZATION_AND_LOCATION]
        .includes(state.page)) {
      throw new Error('Cannot set organization selection if page "' + state.page + '" is active!');
    }

    if (state.type === UserAreaType.SINGLE_SELECTION_OBJECT) {
      throw new Error('Cannot set organization selection when type is SINGLE_SELECTION_OBJECT!');
    }

    ctx.patchState({
      selectedOrganizationIds: new Set([...action.selectedOrganizationIds])
    });
  }

  @Action(UserAreaEditorActions.SetLocationSelection)
  public setLocationSelection(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetLocationSelection
  ): void {
    const state = ctx.getState();

    this.checkIfSaveIsInProgress(state);

    if (!action.selectedLocationIds) {
      throw new Error('Selected location ids cannot be null!');
    }

    this.checkErrorsForSelection(state);

    if (![UserAreaEditorDialog.Page.SELECT_LOCATION, UserAreaEditorDialog.Page.SELECT_ORGANIZATION_AND_LOCATION]
      .includes(state.page)) {
      throw new Error('Cannot set location selection if page "' + state.page + '" is active!');
    }

    if (state.type === UserAreaType.SINGLE_SELECTION_OBJECT) {
      throw new Error('Cannot set location selection when type is SINGLE_SELECTION_OBJECT!');
    }

    ctx.patchState({
      selectedLocationIds: new Set([...action.selectedLocationIds])
    });
  }

  @Action(UserAreaEditorActions.SetClassSelection)
  public setClassSelection(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetClassSelection
  ): void {
    const state = ctx.getState();

    this.checkIfSaveIsInProgress(state);

    if (!action.selectedClassIds) {
      throw new Error('Selected class ids cannot be null!');
    }

    this.checkErrorsForSelection(state);

    if (UserAreaEditorDialog.Page.SELECT_CLASSES !== state.page) {
      throw new Error('Cannot set class selection if page "' + state.page + '" is active!');
    }

    if (state.type === UserAreaType.SINGLE_SELECTION_OBJECT) {
      throw new Error('Cannot set class selection when type is SINGLE_SELECTION_OBJECT!');
    }

    ctx.patchState({
      selectedClassIds: new Set([...action.selectedClassIds].filter(classId => classId !== ClassUtils.INSTANCE_ROOT_CLASS_ID))
    });
  }

  @Action(UserAreaEditorActions.SetAllClassIds)
  public setAllClassIds(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetAllClassIds
  ): void {
    const state = ctx.getState();

    if (!action.allClassIds) {
      throw new Error('All class ids cannot be null!');
    }

    ctx.patchState({
      allClassIds: new Set([...action.allClassIds])
    });
  }

  @Action(UserAreaEditorActions.SetObjectSelection)
  public setObjectSelection(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetObjectSelection
  ): void {
    const state = ctx.getState();

    this.checkIfSaveIsInProgress(state);

    if (!action.selectedObjectIds) {
      throw new Error('Selected object ids cannot be null!');
    }

    this.checkErrorsForSelection(state);

    if (UserAreaEditorDialog.Page.SELECT_OBJECTS !== state.page) {
      throw new Error('Cannot set object selection if page "' + state.page + '" is active!');
    }

    ctx.patchState({
      selectedObjectIds: new Set([...action.selectedObjectIds])
    });
  }

  @Action(UserAreaEditorActions.SetModuleSelection)
  public setModuleSelection(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetModuleSelection
  ): void {
    const state = ctx.getState();

    this.checkIfSaveIsInProgress(state);

    if (!action.selectedModuleIds) {
      throw new Error('Selected module ids cannot be null!');
    }

    this.checkErrorsForSelection(state);

    if (UserAreaEditorDialog.Page.SELECT_MODULES !== state.page) {
      throw new Error('Cannot set module selection if page "' + state.page + '" is active!');
    }

    ctx.patchState({
      selectedModuleIds: new Set([...action.selectedModuleIds])
    });
  }

  @Action(UserAreaEditorActions.Save)
  public save(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.Save
  ): Observable<UserAreaDetailMenuEnum> {
    const state = ctx.getState();

    this.checkIfSaveIsInProgress(state);

    if (!UserAreaEditorXsState.savePossible(state) || !UserAreaEditorXsState.saveEnabled(state)) {
      throw new Error("Save is not enabled!");
    }

    const userAreaId = state.userAreaId;

    let request: Observable<any>;

    switch (state.type) {
      case UserAreaType.COMBINATION: {
        if (state.selectionType) {
          switch (state.selectionType) {
            case UserAreaEditorDialog.SelectionType.CLASS: {
              switch (state.mode) {
                case UserAreaEditorDialog.Mode.ADD: {
                  request = this.userAreaService.addSelection(
                    userAreaId,
                    new Set(),
                    state.selectedClassIds?.size==[...state.allClassIds].filter(classId => classId != ClassUtils.INSTANCE_ROOT_CLASS_ID)?.length
                      ? state.allClassIds
                      : state.selectedClassIds,
                    new Set(),
                    new Set(),
                    new Set()
                  );
                  break;
                }
                case UserAreaEditorDialog.Mode.REMOVE: {
                  request = this.userAreaService.removeSelection(
                    userAreaId,
                    new Set(),
                    state.selectedClassIds?.size==[...state.userArea.classes].filter(c => c.componentClassId != ClassUtils.INSTANCE_ROOT_CLASS_ID).length
                    ? new Set(state.userArea.classes.map(c => c.componentClassId))
                    : state.selectedClassIds,
                    new Set(),
                    new Set(),
                    new Set()
                  );
                  break;
                }
              }
              break;
            }
            case UserAreaEditorDialog.SelectionType.ORG_LOCATION: {
              switch (state.mode) {
                case UserAreaEditorDialog.Mode.ADD: {
                  request = this.userAreaService.addSelection(
                    userAreaId,
                    state.selectedOrganizationIds,
                    new Set(),
                    state.selectedLocationIds,
                    new Set(),
                    new Set()
                  );
                  break;
                }
                case UserAreaEditorDialog.Mode.REMOVE: {
                  request = this.userAreaService.removeSelection(
                    userAreaId,
                    state.selectedOrganizationIds,
                    new Set(),
                    state.selectedLocationIds,
                    new Set(),
                    new Set()
                  );
                  break;
                }
              }
              break;
            }
          }
        }
        break;
      }
      case UserAreaType.SINGLE_SELECTION_OBJECT: {
        switch (state.mode) {
          case UserAreaEditorDialog.Mode.ADD: {
            request = this.userAreaService.addSelection(
              userAreaId,
              new Set<string>(),
              new Set<string>(),
              new Set<string>(),
              state.selectedObjectIds,
              new Set()
            );
            break;
          }
          case UserAreaEditorDialog.Mode.REMOVE: {
            request = this.userAreaService.removeSelection(
              userAreaId,
              new Set<string>(),
              new Set<string>(),
              new Set<string>(),
              state.selectedObjectIds,
              new Set()
            );
            break;
          }
        }
        break;
      }
      case UserAreaType.SINGLE_SELECTION_MODULE: {
        switch (state.mode) {
          case UserAreaEditorDialog.Mode.ADD: {
            request = this.userAreaService.addSelection(
              userAreaId,
              new Set<string>(),
              new Set<string>(),
              new Set<string>(),
              new Set(),
              state.selectedModuleIds
            );
            break;
          }
          case UserAreaEditorDialog.Mode.REMOVE: {
            request = this.userAreaService.removeSelection(
              userAreaId,
              new Set<string>(),
              new Set<string>(),
              new Set<string>(),
              new Set(),
              state.selectedModuleIds
            );
            break;
          }
        }
        break;
      }

    }

    ctx.patchState({
      saveInProgress: true
    });

    return request
      .pipe(
        tap({
          next: () => {
            ctx.patchState({
              saveInProgress: false
            });
            ctx.dispatch(new UserAreaEditorActions.CloseDialog);
          },
          error: () => {
            ctx.patchState({
              saveInProgress: false
            });
          }
        }),
        map(() => action.tabMenuEnum)
    );
  }

  @Action(UserAreaEditorActions.SetAllOrganizationCount)
  public setAllOrganizationCount(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetAllOrganizationCount
  ): void {
    if (ctx.getState().mode === UserAreaEditorDialog.Mode.REMOVE) {
      throw new Error('Cannot set all organization count is "REMOVE" mode!');
    }

    ctx.patchState({allOrganizationCount: action.allOrganizationCount});
  }

  @Action(UserAreaEditorActions.SetAllLocationCount)
  public setAllLocationCount(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetAllLocationCount
  ): void {
    if (ctx.getState().mode === UserAreaEditorDialog.Mode.REMOVE) {
      throw new Error('Cannot set all location count is "REMOVE" mode!');
    }

    ctx.patchState({allLocationCount: action.allLocationCount});
  }

  @Action(UserAreaEditorActions.SetAllClassCount)
  public setAllClassCount(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetAllClassCount
  ): void {
    if (ctx.getState().mode === UserAreaEditorDialog.Mode.REMOVE) {
      throw new Error('Cannot set all class count is "REMOVE" mode!');
    }

    ctx.patchState({allClassCount: action.allClassCount});
  }

  @Action(UserAreaEditorActions.SetAllObjectCount)
  public setAllObjectCount(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetAllObjectCount
  ): void {
    if (ctx.getState().mode === UserAreaEditorDialog.Mode.REMOVE) {
      throw new Error('Cannot set all object count is "REMOVE" mode!');
    }

    ctx.patchState({allObjectCount: action.allObjectCount});
  }

  @Action(UserAreaEditorActions.SetAllModuleCount)
  public setAllModuleCount(
    ctx: StateContext<UserAreaEditorStateModel>,
    action: UserAreaEditorActions.SetAllModuleCount
  ): void {
    // if (ctx.getState().mode === UserAreaEditorDialog.Mode.REMOVE) {
    //   throw new Error('Cannot set all object count is "REMOVE" mode!');
    // }

    ctx.patchState({allModuleCount: action.allModuleCount});
  }

  @Action(UserAreaEditorActions.CloseDialog)
  public closeDialog(
    ctx: StateContext<UserAreaEditorStateModel>
  ): void {
    const state = ctx.getState();

    this.checkIfSaveIsInProgress(state);

    this.dialog2service.closeDialog(Dialog2Type.USER_AREA_EDITOR);
    ctx.setState(getDefaults());
  }

  @Action(ResetStateDefaults)
  public resetDefaults(
    ctx: StateContext<UserAreaEditorStateModel>
  ): void {
    ctx.setState(getDefaults());
  }
  //endregion

  //region HELPERS
  private checkIfSaveIsInProgress(state: UserAreaEditorStateModel): void {
    if (state.saveInProgress) {
      throw new Error('Save is in progress!');
    }
  }

  private checkErrorsForSelection(state: UserAreaEditorStateModel): void {
    if (!state.mode || !state.type || !state.page) {
      throw new Error('Cannot set selection when mode, type or page is not set!');
    }

    if (state.type === UserAreaType.COMBINATION && !state.selectionType) {
      throw new Error('Cannot set selection when selection type is not specified!');
    }
  }
  //endregion
}
